import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { Link, useSearch } from "@tanstack/react-router";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import {
  BanIcon,
  CheckCircleIcon,
  CircleDashedIcon,
  ClockIcon,
  DownloadIcon,
  ExternalLinkIcon,
  WalletIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePerformancePayoutReportQuery } from "../api/queries";
import {
  PerformanceReportDateFilter,
  type PerformanceReportSearch,
  usePerformanceReportDates,
} from "../ui/PerformanceReportDateFilter";
import {
  escapeCsvCell,
  formatPerformanceDateTime,
  formatPerformanceMoney,
  PerformanceReportsNav,
} from "../ui/PerformanceReportsNav";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

export function PerformancePayoutReportPage() {
  const search = useSearch({
    from: "/app/performance/reports/payouts",
  }) as PerformanceReportSearch;

  const { dateFrom, dateTo } = usePerformanceReportDates(search);
  const offset = search.offset ?? 0;
  const limit = 20;

  const query = useMemo(
    () => ({
      dateFrom,
      dateTo,
      status: search.status as
        | "draft"
        | "pending_approval"
        | "approved"
        | "paid"
        | "cancelled"
        | undefined,
      offset,
      limit,
    }),
    [dateFrom, dateTo, search.status, offset],
  );

  const { data, isLoading } = usePerformancePayoutReportQuery(query);
  const rows = data?.data ?? [];
  const summary = data?.summary;
  const total = data?.total ?? 0;

  const exportCsv = useCallback(() => {
    const header = "ເລກທີ,ນັກສະແດງ,ງານ,ວັນທີງານ,ສະຖານະ,ຍອດ,ວັນທີຈ່າຍ";
    const csvRows = rows.map((row) =>
      [
        escapeCsvCell(row.payoutNo),
        escapeCsvCell(row.performerName),
        escapeCsvCell(row.eventTitle),
        escapeCsvCell(formatPerformanceDateTime(row.eventDate)),
        escapeCsvCell(row.status),
        escapeCsvCell(row.totalAmount),
        escapeCsvCell(row.paidAt ? formatPerformanceDateTime(row.paidAt) : ""),
      ].join(","),
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-payouts-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, dateFrom, dateTo]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ລາຍງານງານສະແດງ", to: "/app/performance/reports" },
            { label: "ຈ່າຍນັກສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານຈ່າຍນັກສະແດງ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຍອດໃບເບີກຈ່າຍຕາມສະຖານະ ແລະ ນักแสด
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <PerformanceReportsNav />

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PerformanceReportDateFilter
            routeFrom="/app/performance/reports/payouts"
            showPayoutStatus
          />
        </div>

        <ReportSummaryStatGrid columns={5}>
          {(
            [
              ["draft", summary?.byStatus.draft, CircleDashedIcon, "packages"],
              [
                "pending_approval",
                summary?.byStatus.pendingApproval,
                ClockIcon,
                "attendance",
              ],
              [
                "approved",
                summary?.byStatus.approved,
                CheckCircleIcon,
                "revenue",
              ],
              ["paid", summary?.byStatus.paid, WalletIcon, "students"],
              ["cancelled", summary?.byStatus.cancelled, BanIcon, "refunds"],
            ] as const
          ).map(([key, amount, Icon, tone]) => (
            <ReportSummaryStatCard
              key={key}
              title={STATUS_LABELS[key]}
              icon={Icon}
              tone={tone}
              isLoading={isLoading}
              value={<CompactMoneyAmount amount={amount ?? 0} />}
            />
          ))}
        </ReportSummaryStatGrid>

        {summary && summary.topPerformers.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Top 5 ນັກສະແດງ (ຈ່າຍແລ້ວ)</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {summary.topPerformers.map((item) => (
                  <li
                    key={item.performerId}
                    className="flex items-center justify-between"
                  >
                    <span>{item.performerName}</span>
                    <span className="font-medium">
                      {formatPerformanceMoney(item.totalPaid)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງໃບເບີກຈ່າຍ ({total})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</p>
            ) : rows.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີທີ່ເລືອກ
              </p>
            ) : (
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ເລກທີ</th>
                    <th className="pr-4 pb-2 font-medium">ນັກສະແດງ</th>
                    <th className="pr-4 pb-2 font-medium">ງານ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີງານ</th>
                    <th className="pr-4 pb-2 font-medium">ສະຖານະ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ຍອດ</th>
                    <th className="pb-2 font-medium">ວັນທີຈ່າຍ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/payouts/$id"
                          params={{ id: row.id }}
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                          {row.payoutNo}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="py-3 pr-4">{row.performerName}</td>
                      <td className="py-3 pr-4">{row.eventTitle}</td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.eventDate)}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={
                            STATUS_VARIANTS[
                              row.status as keyof typeof STATUS_VARIANTS
                            ] ?? "secondary"
                          }
                        >
                          {STATUS_LABELS[
                            row.status as keyof typeof STATUS_LABELS
                          ] ?? row.status}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {formatPerformanceMoney(row.totalAmount)}
                      </td>
                      <td className="whitespace-nowrap py-3">
                        {formatPerformanceDateTime(row.paidAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
